import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import type { ScheduleType } from '@tsuyosa/types';
import { useAuth } from '../context/AuthContext';
import { getAdminScheduleTypes, createScheduleType, updateScheduleType } from '../lib/api';
import { Spinner, Button } from '@tsuyosa/ui';

type FormState = Pick<ScheduleType, 'name_id' | 'name_jp' | 'sort_order' | 'is_active'>;

const DEFAULT_FORM: FormState = {
  name_id:    '',
  name_jp:    '',
  sort_order: 0,
  is_active:  true,
};

export default function ScheduleTypeFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEdit);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit || !accessToken || !id) return;
    getAdminScheduleTypes(accessToken)
      .then((data) => {
        const found = data.find((t) => t.id === id);
        if (found) setForm({ name_id: found.name_id, name_jp: found.name_jp, sort_order: found.sort_order, is_active: found.is_active });
      })
      .finally(() => setIsFetching(false));
  }, [isEdit, accessToken, id]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!accessToken) return;
    setError('');
    setIsLoading(true);
    try {
      if (isEdit && id) {
        await updateScheduleType(accessToken, id, form);
      } else {
        await createScheduleType(accessToken, form);
      }
      navigate('/schedule-types');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  }

  if (isFetching) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/schedule-types')}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-white/5 hover:text-gray-200"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-gray-100">
          {isEdit ? 'Edit Schedule Type' : 'Add Schedule Type'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">
            Nama (Bahasa Indonesia) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            required
            value={form.name_id}
            onChange={(e) => setForm((prev) => ({ ...prev, name_id: e.target.value }))}
            placeholder="e.g. Konser"
            className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">
            名前（日本語） <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            required
            value={form.name_jp}
            onChange={(e) => setForm((prev) => ({ ...prev, name_jp: e.target.value }))}
            placeholder="e.g. コンサート"
            className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none"
          />
        </div>

        <div className="flex flex-wrap gap-5">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Sort Order</label>
            <input
              type="number"
              min={0}
              value={form.sort_order}
              onChange={(e) => setForm((prev) => ({ ...prev, sort_order: Number(e.target.value) }))}
              className="w-24 rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none"
            />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex cursor-pointer items-center gap-2.5">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                className="h-4 w-4 rounded accent-pink-500"
              />
              <span className="text-sm text-gray-300">Aktif (muncul di dropdown schedule)</span>
            </label>
          </div>
        </div>

        {error && (
          <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</p>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={() => navigate('/schedule-types')}>
            Cancel
          </Button>
          <Button type="submit" variant="tsuyosa" size="sm" isLoading={isLoading}>
            {isEdit ? 'Save Changes' : 'Add Type'}
          </Button>
        </div>
      </form>
    </div>
  );
}
